import { useMemo } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { FEATURES } from '../../data/conjointData';
import { calculatePnL } from '../../lib/calculations';
import { formatPercent, formatCurrencyCompact } from '../../lib/formatters';
import { InfoButton } from '../common/Tooltip';
import type { PackageConfig } from '../../types';

export function FeatureImpactMatrix() {
  const selectedPrice = useSimulatorStore(s => s.selectedPrice);
  const assumptions = useSimulatorStore(s => s.assumptions);

  const matrix = useMemo(() => {
    const baseConfig: PackageConfig = { name: 'base', price: selectedPrice, paymentTerm: 'monthlyZeroApr', enabledFeatures: [] };
    const basePnL = calculatePnL(baseConfig, assumptions);
    return FEATURES.map(f => {
      const pnl = calculatePnL({ name: 'with', price: selectedPrice, paymentTerm: 'monthlyZeroApr', enabledFeatures: [f.name] }, assumptions);
      return { name: f.name, choiceRateImpact: pnl.adjustedChoiceRate - basePnL.adjustedChoiceRate, revenueImpact: pnl.netRevenue - basePnL.netRevenue, pnlImpact: pnl.monthlyPnL - basePnL.monthlyPnL };
    });
  }, [selectedPrice, assumptions]);

  const maxCR = Math.max(...matrix.map(m => Math.abs(m.choiceRateImpact)));
  const maxRev = Math.max(...matrix.map(m => Math.abs(m.revenueImpact)));
  const maxPnL = Math.max(...matrix.map(m => Math.abs(m.pnlImpact)));

  function heatColor(value: number, max: number): string {
    const t = Math.min(Math.abs(value) / max, 1);
    return value >= 0 ? `rgba(16, 185, 129, ${0.1 + t * 0.5})` : `rgba(239, 68, 68, ${0.1 + t * 0.5})`;
  }

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Feature Impact Matrix</h3>
        <InfoButton helpKey="featureImpactMatrix" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">Incremental impact of adding each feature alone (vs. no features) at {formatCurrencyCompact(selectedPrice)}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-secondary">
              <th className="text-left py-2 px-2 font-medium">Feature</th>
              <th className="text-right py-2 px-2 font-medium"><span className="inline-flex items-center gap-1">Conversion Change <InfoButton helpKey="choiceRateDelta" size={10} /></span></th>
              <th className="text-right py-2 px-2 font-medium">Revenue Δ (scenario)</th>
              <th className="text-right py-2 px-2 font-medium">P&L Δ (scenario)</th>
            </tr>
          </thead>
          <tbody>
            {matrix.map(row => (
              <tr key={row.name} className="border-b border-border/50">
                <td className="py-2 px-2 text-primary">{row.name}</td>
                <td className="text-right py-2 px-2 font-mono text-primary" style={{ backgroundColor: heatColor(row.choiceRateImpact, maxCR) }}>+{formatPercent(row.choiceRateImpact)}</td>
                <td className="text-right py-2 px-2 font-mono text-primary" style={{ backgroundColor: heatColor(row.revenueImpact, maxRev) }}>{formatCurrencyCompact(row.revenueImpact)}</td>
                <td className="text-right py-2 px-2 font-mono text-primary" style={{ backgroundColor: heatColor(row.pnlImpact, maxPnL) }}>{formatCurrencyCompact(row.pnlImpact)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
